// Draft autosave bridge (M3-P07 / U16). Forwards local engine mutations to a
// host endpoint as debounced batches of ops in the `ui--flow-sync` vocabulary
// (addNode removeNode moveNode addEdge removeEdge updateEdge updateNode).
//
// Protocol (host contract, plan §5.1):
//   PATCH <url>  { lock_version, request_id, ops: [...] }
//   200          { lock_version, problems: [...], corrections: [...] }
//   409          { lock_version }   — stale draft: nothing was applied
//
// - ONE request in flight at a time. Ops raised meanwhile queue and coalesce
//   (consecutive moveNode on one id collapse to the last). The lock_version
//   from each response rides the next request.
// - A network failure or 5xx retries the SAME request (same request_id,
//   lock_version, ops) so the server can answer idempotently.
// - 409 halts the bridge: queue discarded, `:conflict` emitted. The host
//   reloads (fromObject) and calls resume(lockVersion) to continue.
// - Server `corrections` apply under an echo guard and never persist back.
//
// Node `data` never leaves the canvas (config is edited server-side), so
// addNode/updateNode ops carry the node def minus `data`.
//
// Events (bubbling, on the canvas element): ui--flow-persist:problems
// {problems, lockVersion}, :conflict {lockVersion}, :error {status, body}.
import { Controller } from "@hotwired/stimulus"

const RETRYABLE = (status) => status >= 500

export default class extends Controller {
  static values = {
    url: String,
    lockVersion: { type: Number, default: 0 },
    debounce: { type: Number, default: 400 },
    retryDelay: { type: Number, default: 1000 },
    maxRetries: { type: Number, default: 3 }
  }
  static outlets = ["ui--flow"]

  connect() {
    this._applying = false
    this._halted = false
    this._inFlight = false
    this._queue = []
    this._pending = null // the batch being (re)sent: {request_id, lock_version, ops}
    this._timer = null
    this._connected = true
    this._bind()
  }

  disconnect() {
    this._connected = false
    clearTimeout(this._timer)
    this._timer = null
    this._unbind()
    this._queue = []
  }

  // Host calls this after reloading the graph from the server on a conflict.
  resume(lockVersion) {
    if (lockVersion !== undefined && lockVersion !== null) this.lockVersionValue = Number(lockVersion)
    this._halted = false
  }

  // Apply server-originated ops (corrections) without echoing them back.
  applyOps(ops) {
    const flow = this.hasUiFlowOutlet ? this.uiFlowOutlet : null
    if (!flow || !Array.isArray(ops)) return
    this._applying = true
    try {
      ops.forEach((op, i) => {
        try { this._applyOne(flow, op) } catch (err) {
          console.warn(`[ui--flow-persist] correction #${i} (${op && op.op}) failed to apply — skipped`, err)
        }
      })
    } finally {
      this._applying = false
    }
  }

  _applyOne(flow, op) {
    if (!op || typeof op !== "object") return
    const { op: name, ...rest } = op
    switch (name) {
      case "addNode": flow.addNode?.(op.node); break
      case "updateNode": flow.replaceNode?.(op.node); break
      case "removeNode": flow.removeNode?.(op.id); break
      case "moveNode": flow.moveNode?.(op.id, op.position?.x, op.position?.y); break
      case "addEdge": flow.addEdge?.(rest); break
      case "updateEdge": flow.replaceEdge?.(rest); break
      case "removeEdge": flow.removeEdge?.(op.id); break
      default: console.warn(`[ui--flow-persist] unknown correction op "${name}"`, op)
    }
  }

  // ── local → queue ────────────────────────────────────────────────────────

  _bind() {
    const el = this.hasUiFlowOutlet ? this.uiFlowOutlet.element : null
    if (!el) return
    this._el = el
    const flow = this.uiFlowOutlet
    this._handlers = {
      "ui--flow:nodemove": (e) => this._enqueue({ op: "moveNode", id: e.detail.nodeId, position: { x: e.detail.x, y: e.detail.y } }),
      "ui--flow:nodedelete": (e) => this._enqueue({ op: "removeNode", id: e.detail.nodeId }),
      "ui--flow:edgedelete": (e) => this._enqueue({ op: "removeEdge", id: e.detail.edgeId }),
      "ui--flow:nodesadd": (e) => (Array.isArray(e.detail?.nodes) ? e.detail.nodes : [])
        .forEach(node => this._enqueue({ op: "addNode", node: this._stripData(node) })),
      "ui--flow:edgesadd": (e) => (Array.isArray(e.detail?.edges) ? e.detail.edges : [])
        .forEach(edge => this._enqueue({ op: "addEdge", ...edge })),
      "ui--flow:edgechange": (e) => {
        const edge = e.detail?.edgeId ? flow.getEdge?.(e.detail.edgeId) : null
        if (edge) this._enqueue({ op: "updateEdge", ...edge })
      },
      "ui--flow:nodechange": (e) => {
        const node = e.detail?.nodeId ? flow.getNode?.(e.detail.nodeId) : null
        if (node) this._enqueue({ op: "updateNode", node: this._stripData(node) })
      }
    }
    Object.entries(this._handlers).forEach(([name, fn]) => el.addEventListener(name, fn))
  }

  _unbind() {
    if (!this._el) return
    Object.entries(this._handlers).forEach(([name, fn]) => this._el.removeEventListener(name, fn))
    this._el = null
  }

  _stripData(node) {
    const { data, ...rest } = node // eslint-disable-line no-unused-vars
    return rest
  }

  _enqueue(op) {
    if (this._applying || this._halted || !this._connected) return
    if (op.op === "moveNode") {
      // Coalesce with a not-yet-sent moveNode on the same id: last position wins.
      const existing = this._queue.find(q => q.op === "moveNode" && q.id === op.id)
      if (existing) { existing.position = op.position; this._schedule(); return }
    }
    this._queue.push(op)
    this._schedule()
  }

  _schedule() {
    if (this._inFlight || this._pending) return // the response handler flushes
    clearTimeout(this._timer)
    this._timer = setTimeout(() => this._flush(), this.debounceValue)
  }

  // ── queue → server ───────────────────────────────────────────────────────

  _flush() {
    this._timer = null
    if (this._inFlight || this._halted || !this._connected) return
    if (!this._pending) {
      if (this._queue.length === 0) return
      this._pending = { request_id: this._uuid(), lock_version: this.lockVersionValue, ops: this._queue }
      this._queue = []
      this._attempt = 0
    }
    this._send()
  }

  async _send() {
    this._inFlight = true
    let response
    try {
      response = await fetch(this.urlValue, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrf()
        },
        body: JSON.stringify(this._pending)
      })
    } catch (err) {
      this._inFlight = false
      if (!this._connected) return
      return this._retryOrFail({ status: 0, body: { message: String(err && err.message || err) } })
    }
    let body = null
    try { body = await response.json() } catch (_) { body = null }
    this._inFlight = false
    if (!this._connected) return

    if (response.ok) return this._succeeded(body || {})
    if (response.status === 409) return this._conflicted(body || {})
    if (RETRYABLE(response.status)) return this._retryOrFail({ status: response.status, body })
    this._failed({ status: response.status, body })
  }

  _succeeded(body) {
    this._pending = null
    if (typeof body.lock_version === "number") this.lockVersionValue = body.lock_version
    const problems = Array.isArray(body.problems) ? body.problems : []
    this._dispatch("problems", { problems, lockVersion: this.lockVersionValue })
    if (Array.isArray(body.corrections) && body.corrections.length) this.applyOps(body.corrections)
    if (this._queue.length) this._schedule()
  }

  _conflicted(body) {
    this._pending = null
    this._queue = []
    this._halted = true
    clearTimeout(this._timer)
    this._dispatch("conflict", { lockVersion: typeof body.lock_version === "number" ? body.lock_version : this.lockVersionValue })
  }

  _retryOrFail(error) {
    if (this._attempt < this.maxRetriesValue) {
      this._attempt += 1
      this._timer = setTimeout(() => this._flush(), this.retryDelayValue)
      return
    }
    this._failed(error)
  }

  // Non-retryable (or retries exhausted): drop the batch so the bridge keeps working.
  _failed(error) {
    this._pending = null
    this._dispatch("error", error)
    if (this._queue.length) this._schedule()
  }

  _dispatch(name, detail) {
    this.element.dispatchEvent(new CustomEvent(`ui--flow-persist:${name}`, { detail, bubbles: true }))
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _uuid() {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
    return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  }
}
